import BikeColor from "../BikeColor";
import PropTypes from "prop-types";
import { Box, Chip } from "@mui/material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import { formatDate, formatTime } from "../../utils/dateFormat";

// ============ CELDAS DEL HISTÓRICO ============
const HistIdCell = ({ value }) => (
  <MDTypography variant="caption" fontWeight="bold" color="info">
    #{value}
  </MDTypography>
);

HistIdCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const HistFechaCell = ({ value }) => {
  if (!value) return <MDTypography variant="caption">—</MDTypography>;
  return (
    <MDBox>
      <MDTypography
        variant="caption"
        display="block"
        fontWeight="medium"
        sx={{ color: "#1e293b", fontSize: "0.8rem" }}
      >
        {formatDate(value)}
      </MDTypography>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
        {formatTime(value)}
      </MDTypography>
    </MDBox>
  );
};

HistFechaCell.propTypes = { value: PropTypes.string };
HistFechaCell.defaultProps = { value: null };

const HistUserCell = ({ row }) => {
  const originalRow = row?.original || row;
  const nombre = originalRow?.nombre || "";
  const cedula = originalRow?.cedula || "";
  const celular = originalRow?.celular || "";

  return (
    <MDBox>
      <MDTypography
        variant="caption"
        display="block"
        fontWeight="600"
        sx={{ color: "#1e293b", fontSize: "0.8rem" }}
      >
        {nombre || `Usuario #${originalRow?.id_user || "—"}`}
      </MDTypography>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
        C.C: {cedula || "—"}
        {celular ? ` | Cel: ${celular}` : ""}
      </MDTypography>
    </MDBox>
  );
};

HistUserCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id_user: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nombre: PropTypes.string,
      cedula: PropTypes.string,
      celular: PropTypes.string,
    }),
    id_user: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nombre: PropTypes.string,
    cedula: PropTypes.string,
    celular: PropTypes.string,
  }),
};

HistUserCell.defaultProps = { row: {} };

const HistBiciCell = ({ row }) => {
  const originalRow = row?.original || row;
  const code = originalRow?.serie || originalRow?.code || "";
  const color = originalRow?.color || "";
  const marca = originalRow?.marca || "";

  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" gap={0.75}>
        <BikeColor value={color} showLabel={false} />
        <MDTypography
          variant="caption"
          display="block"
          fontWeight="600"
          sx={{ color: "#1e293b", fontSize: "0.8rem" }}
        >
          {code || `Bici #${originalRow?.id_byke || "—"}`}
        </MDTypography>
      </MDBox>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
        {color || "—"}
        {marca ? ` | ${marca}` : ""}
      </MDTypography>
    </MDBox>
  );
};

HistBiciCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id_byke: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      serie: PropTypes.string,
      code: PropTypes.string,
      color: PropTypes.string,
      marca: PropTypes.string,
    }),
    id_byke: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    serie: PropTypes.string,
    code: PropTypes.string,
    color: PropTypes.string,
    marca: PropTypes.string,
  }),
};

HistBiciCell.defaultProps = { row: {} };

const HistGanchoCell = ({ value }) => {
  if (!value && value !== 0) {
    return (
      <MDTypography variant="caption" color="text.secondary">
        —
      </MDTypography>
    );
  }
  return (
    <Chip
      size="small"
      label={`#${String(value).padStart(2, "0")}`}
      sx={{
        fontWeight: "bold",
        bgcolor: "#e3f2fd",
        color: "#1565c0",
        border: "1px solid #90caf9",
      }}
    />
  );
};

HistGanchoCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

HistGanchoCell.defaultProps = { value: null };

const HistEstadoCell = ({ value }) => {
  const isFinalizado = Boolean(value);
  return (
    <Chip
      size="small"
      label={isFinalizado ? "FINALIZADO" : "ACTIVO"}
      sx={{
        height: 22,
        fontSize: "0.65rem",
        fontWeight: "bold",
        bgcolor: isFinalizado ? "#e8f5e9" : "#fff3e0",
        color: isFinalizado ? "#2e7d32" : "#e65100",
      }}
    />
  );
};

HistEstadoCell.propTypes = { value: PropTypes.bool };
HistEstadoCell.defaultProps = { value: false };

// ============ CELDAS DEL PARQUEADERO ============
const PrkgIdCell = ({ value }) => (
  <MDTypography variant="caption" fontWeight="bold" color="info">
    #{value}
  </MDTypography>
);

PrkgIdCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const PrkgUserCell = ({ row }) => {
  const originalRow = row?.original || row;
  const nombre = originalRow?.nombre || "";
  const cedula = originalRow?.cedula || "";
  const celular = originalRow?.celular || "";

  return (
    <MDBox>
      <MDTypography
        variant="caption"
        display="block"
        fontWeight="600"
        sx={{ color: "#1e293b", fontSize: "0.8rem" }}
      >
        {nombre || `Usuario #${originalRow?.id_user || "—"}`}
      </MDTypography>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
        C.C: {cedula || "—"}
        {celular ? ` | Cel: ${celular}` : ""}
      </MDTypography>
    </MDBox>
  );
};

PrkgUserCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id_user: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nombre: PropTypes.string,
      cedula: PropTypes.string,
      celular: PropTypes.string,
    }),
    id_user: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nombre: PropTypes.string,
    cedula: PropTypes.string,
    celular: PropTypes.string,
  }),
};

PrkgUserCell.defaultProps = { row: {} };

const PrkgBiciCell = ({ row }) => {
  const originalRow = row?.original || row;
  const code = originalRow?.code || "";
  const color = originalRow?.color || "";
  const marca = originalRow?.marca || "";

  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" gap={0.75}>
        <BikeColor value={color} showLabel={false} />
        <MDTypography
          variant="caption"
          display="block"
          fontWeight="600"
          sx={{ color: "#1e293b", fontSize: "0.8rem" }}
        >
          {code || `Bici #${originalRow?.id_byke || "—"}`}
        </MDTypography>
      </MDBox>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
        {color || "—"}
        {marca ? ` | ${marca}` : ""}
      </MDTypography>
    </MDBox>
  );
};

PrkgBiciCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      bikeAssociationNeedsReview: PropTypes.bool,
      id_byke: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      code: PropTypes.string,
      color: PropTypes.string,
      marca: PropTypes.string,
    }),
    id_byke: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bikeAssociationNeedsReview: PropTypes.bool,
    code: PropTypes.string,
    color: PropTypes.string,
    marca: PropTypes.string,
  }),
};

PrkgBiciCell.defaultProps = { row: {} };

const PrkgHarnessCell = ({ value }) => {
  if (!value && value !== 0) {
    return (
      <MDTypography variant="caption" color="text.secondary">
        —
      </MDTypography>
    );
  }
  return (
    <Chip
      size="small"
      label={`#${String(value).padStart(2, "0")}`}
      sx={{
        fontWeight: "bold",
        bgcolor: "#e8f5e9",
        color: "#2e7d32",
        border: "1px solid #a5d6a7",
      }}
    />
  );
};

PrkgHarnessCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PrkgHarnessCell.defaultProps = { value: null };

const PrkgEnterAtCell = ({ value }) => {
  if (!value) return <MDTypography variant="caption">—</MDTypography>;
  return (
    <MDBox>
      <MDTypography
        variant="caption"
        display="block"
        fontWeight="medium"
        sx={{ color: "#1e293b", fontSize: "0.8rem" }}
      >
        {formatDate(value)}
      </MDTypography>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
        {formatTime(value)}
      </MDTypography>
    </MDBox>
  );
};

PrkgEnterAtCell.propTypes = { value: PropTypes.string };
PrkgEnterAtCell.defaultProps = { value: null };

export const historicoColumns = [
  { Header: "#", accessor: "id", width: "5%", Cell: HistIdCell },
  { Header: "Usuario", accessor: "usuario", width: "25%", Cell: HistUserCell },
  { Header: "Bicicleta", accessor: "bicicleta", width: "20%", Cell: HistBiciCell },
  { Header: "Gancho", accessor: "ganchoId", width: "8%", Cell: HistGanchoCell },
  { Header: "Entrada", accessor: "entrada", width: "14%", Cell: HistFechaCell },
  { Header: "Salida", accessor: "salida", width: "14%", Cell: HistFechaCell },
  { Header: "Estado", accessor: "tieneSalida", width: "14%", Cell: HistEstadoCell },
];

export const prkgColumns = [
  { Header: "#", accessor: "id", width: "8%", Cell: PrkgIdCell },
  { Header: "Usuario", accessor: "usuario", width: "30%", Cell: PrkgUserCell },
  { Header: "Bicicleta", accessor: "bicicleta", width: "28%", Cell: PrkgBiciCell },
  { Header: "Gancho", accessor: "harness", width: "12%", Cell: PrkgHarnessCell },
  { Header: "Entrada", accessor: "enter_at", width: "22%", Cell: PrkgEnterAtCell },
];
